import { ReactNode } from "react";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { appTokens } from "./Styles/appTokens";
import { useCommonStyles } from "./Styles/commonStyles";
import { useThemeState } from "../../redux/reducers/themeReducer";
import { useSpacingUtilities } from "../../utilities/useSpacingUtilities";

const useStyles = makeStyles({
    root: {
        flexGrow: '1',
        flexShrink: '1',
        flexBasis: '0',
        boxSizing: 'border-box',
        maxHeight: '100%',
        maxWidth: '100%',
        position: 'relative',
    },
    widthOrHeightSet: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 'unset',
    },
    alignLeft: {
        alignItems: 'start',
    },
    alignRight: {
        alignItems: 'end',
    },
    alignCenter: {
        alignItems: 'center',
    },
    alignMiddle: {
        justifyContent: 'center',
    },
    alignBottom: {
        justifyContent: 'end',
    },
    noGap: {
        gap: '0rem',
    },
    smallGap: {
        gap: '.25rem',
    },
    mediumGap: {
        gap: '.75rem',
    },
    largeGap: {
        gap: '1.5rem',
    },
    scroll: {
        overflowY: 'auto',
        overflowX: 'auto',
        scrollbarWidth: 'thin',
    },
    border: {
        border: `1px solid ${appTokens.border.colors.brand}`,
    },
    shadowDark: {
        boxShadow: 'var(--shadow16Brand)',
    },
    shadowLight: {
        boxShadow: 'var(--shadow16Brand)',
    },
    shadowSmallDark: {
        boxShadow: 'var(--shadow8Brand)',
    },
    shadowSmallLight: {
        boxShadow: 'var(--shadow8Brand)',
    },
    subtleBorder: {
        border: `1px solid ${appTokens.border.colors.subtle}`,
    },
    bottomBorder: {
        borderBottom: `1px solid ${appTokens.border.colors.subtle}`,
    },
    clickable: {
        cursor: 'pointer',
    },
});
export type ColumnProps = Partial<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> & {
    children?: ReactNode;
    className?: string;
    width?: string;
    height?: string;
    minHeight?: string;
    maxHeight?: string;
    minWidth?: string;
    maxWidth?: string,
    align?: 'left' | 'right' | 'center' | undefined;
    vAlign?: 'top' | 'middle' | 'bottom' | undefined;
    noGap?: boolean;
    gap?: 'small' | 'medium' | 'large';
    scroll?: boolean;
    margin?: string;
    padding?: string;
    border?: boolean;
    flex?: string | number;
    shadow?: boolean;
    shadowSmall?: boolean;
    backgroundColor?: string;
    borderRadius?: string;
    borderStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    borderTopStyle?: string;
    borderBottomStyle?: string;
    overflowX?: React.CSSProperties['overflowX'];
    overflowY?: React.CSSProperties['overflowY'];
    subtleBorder?: boolean;
    bottomBorder?: boolean;
    innerRef?: React.Ref<HTMLDivElement>;
}
export const Column = (props: ColumnProps) => {

    const { children, shadow = false, shadowSmall = false, backgroundColor,
        borderRadius, flex, className,
        width, minWidth, maxWidth,
        height, minHeight, maxHeight,
        align, vAlign = 'top',
        gap = 'large', noGap = false,
        scroll = false,
        margin, padding,
        border = false,
        overflowX, overflowY,
        subtleBorder,
        bottomBorder,
        innerRef,
        borderStyle,
        borderLeftStyle,
        borderRightStyle,
        borderTopStyle,
        borderBottomStyle,
        ...otherProps
    } = props;

    //Hooks
    const { isDarkTheme } = useThemeState();
    const spacing = useSpacingUtilities();

    //Styles
    const commonStyles = useCommonStyles();
    const colStyles = useStyles();
    const columnClasses = mergeClasses(
        commonStyles.utility.flexColumn,
        commonStyles.scroll.classes.scrollbarClasses,
        colStyles.root,
        commonStyles.button.childrenButtons,
        (width || minWidth || maxWidth || height || minHeight || maxHeight) && colStyles.widthOrHeightSet,
        align === 'left' && colStyles.alignLeft,
        align === 'right' && colStyles.alignRight,
        align === 'center' && colStyles.alignCenter,
        vAlign === 'middle' && colStyles.alignMiddle,
        vAlign === 'bottom' && colStyles.alignBottom,
        gap == 'large' && colStyles.largeGap,
        gap == 'medium' && colStyles.mediumGap,
        gap == 'small' && colStyles.smallGap,
        !!noGap && colStyles.noGap,
        ...spacing.getSpacingClasses(padding, margin),
        !!scroll && colStyles.scroll,
        !!border && colStyles.border,
        !!shadow && isDarkTheme && colStyles.shadowDark,
        !!shadow && !isDarkTheme && colStyles.shadowLight,
        !!shadowSmall && isDarkTheme && colStyles.shadowSmallDark,
        !!shadowSmall && !isDarkTheme && colStyles.shadowSmallLight,
        !!subtleBorder && colStyles.subtleBorder,
        !!bottomBorder && colStyles.bottomBorder,
        className,
    );

    return (
        <div
            style={{
                width, minWidth, maxWidth,
                height, minHeight, maxHeight,
                scrollbarWidth: 'thin', flex,
                backgroundColor, borderRadius, border:borderStyle,
                borderLeft:borderLeftStyle,
                borderRight:borderRightStyle,
                borderTop:borderTopStyle,
                borderBottom:borderBottomStyle,
                overflowX, overflowY
            }}
            className={columnClasses}
            ref={innerRef}
            {...otherProps}
        >
            {children}
        </div>
    );
}
